import styled, { css, keyframes } from 'styled-components'

interface MessageProps {
  $role: 'user' | 'assistant'
}

interface MessageBubbleProps extends MessageProps {
  $failed: boolean
}

const enterPanel = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const pulseDot = keyframes`
  0%, 60%, 100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-3px);
  }
`

export const ChatRoot = styled.div<{ $embedded?: boolean }>`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1050;

  @media (max-width: 767px) {
    right: 12px;
    bottom: 12px;
  }
  ${({ $embedded }) => $embedded && css`
    position: relative;
    inset: auto;
    z-index: auto;
    min-width: 0;
    min-height: 0;
    height: 100%;
    @media (max-width: 767px) { inset: auto; }
  `}
`

export const ChatLauncher = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: var(--blue-darker);
  color: white;
  box-shadow: 0 10px 28px rgb(0 45 105 / 24%);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: transform 160ms ease, box-shadow 160ms ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 13px 32px rgb(0 45 105 / 30%);
  }

  &:focus-visible {
    outline: 3px solid rgb(22 119 255 / 28%);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const ChatPanel = styled.section<{ $embedded?: boolean }>`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  box-sizing: border-box;
  width: min(420px, calc(100vw - 48px));
  height: min(640px, calc(100dvh - 48px));
  overflow: hidden;
  border: 1px solid #dde1e7;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 22px 58px rgb(21 36 58 / 18%);
  transform-origin: bottom right;
  animation: ${enterPanel} 200ms ease-out;

  @media (max-width: 767px) {
    width: calc(100vw - 24px);
    height: min(70dvh, 580px);
    border-radius: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  ${({ $embedded }) => $embedded && css`
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    animation: none;
    @media (max-width: 767px) {
      width: 100%;
      height: 100%;
      border-radius: 0;
    }
  `}
`

export const ChatHeader = styled.header<{ $embedded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  min-width: 0;
  padding: 14px 16px;
  border-bottom: 1px solid #eef0f3;
  background: white;
  color: #1c2028;
  ${({ $embedded }) => $embedded && css`
    min-height: 72px;
    padding: 16px 24px;
    border-bottom-color: #edf1f6;
    @media (max-width: 767px) { min-height: 72px; gap: 4px; padding: 12px; }
  `}
`

export const ChatHeading = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
`

export const ChatAvatar = styled.img`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`

export const ChatTitle = styled.span<{ $embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  gap: 2px;
  overflow: hidden;
  cursor: default;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #1c2028;
    font-size: 14px;
    font-weight: 650;
    line-height: 1.25;
  }

  span {
    color: #7a818c;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.3;
  }
  ${({ $embedded }) => $embedded && css`
    strong { color: #102449; font-family: var(--font-montserrat); font-size: 16px; font-weight: 700; }
  `}
`

export const BetaBadge = styled.span`
  flex: 0 0 auto;
  padding: 2px 8px;
  border: 1px solid #e1e4e8;
  border-radius: 999px;
  color: #606875;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.5;
`

export const HeaderActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
`

export const HeaderMenuButton = styled.button`
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #4f5662;
  cursor: pointer;
  transition: background 150ms ease, transform 150ms ease;

  svg {
    transform: rotate(90deg);
  }

  &:hover,
  &:focus-visible {
    background: #f0f1f3;
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid rgb(22 119 255 / 28%);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

export const HeaderButton = styled.button`
  display: grid;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 0;
  border-radius: 9px;
  background: #f0f1f3;
  color: #4f5662;
  cursor: pointer;
  transition: background 150ms ease, transform 150ms ease;

  &:hover,
  &:focus-visible {
    background: #e5e7ea;
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid rgb(22 119 255 / 28%);
    outline-offset: 2px;
  }
`

export const MessagesArea = styled.div`
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px 18px;
  background: white;
  overscroll-behavior: contain;
`

export const HistorySkeleton = styled.div`
  padding: 12px 0;
`

export const EmptyState = styled.div`
  display: flex;
  box-sizing: border-box;
  min-height: 100%;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 0;
  padding: 8px 0 12px;
  text-align: left;

  > strong {
    color: #171a20;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.35;
    text-wrap: balance;
  }

  > span {
    display: block;
    margin: 18px 0 8px;
    color: #7a818c;
    font-size: 13px;
  }
`

export const SuggestionList = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`

export const SuggestionButton = styled.button`
  box-sizing: border-box;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  background: #f2f3f5;
  color: #252a32;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.35;
  text-align: left;
  transition: background 150ms ease, color 150ms ease, transform 150ms ease;

  &:hover,
  &:focus-visible {
    background: #e9eef6;
    color: var(--blue-darker);
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid rgb(22 119 255 / 25%);
    outline-offset: 2px;
  }
`

export const MessageRow = styled.div<MessageProps>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => $role === 'user' ? 'flex-end' : 'flex-start'};
  margin-bottom: 14px;
`

export const MessageBubble = styled.div<MessageBubbleProps>`
  max-width: 92%;
  font-size: 13px;
  line-height: 1.55;
  overflow-wrap: anywhere;

  ${({ $role, $failed }) => $role === 'user'
    ? css`
      padding: 9px 12px;
      border-radius: 13px;
      border-bottom-right-radius: 4px;
      background: #eef1f5;
      color: #202630;
    `
    : css`
      padding: ${$failed ? '10px 12px' : '4px 2px'};
      border: ${$failed ? '1px solid #ffd5d5' : '0'};
      border-radius: ${$failed ? '12px' : '0'};
      background: ${$failed ? '#fff5f5' : 'transparent'};
      color: #253044;
    `}

  p {
    margin: 0 0 8px;
  }

  p:last-child,
  ul:last-child,
  ol:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 6px 0 8px;
    padding-left: 18px;
  }
`

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 20px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #7d8ba1;
    animation: ${pulseDot} 1.2s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 120ms;
  }

  span:nth-child(3) {
    animation-delay: 240ms;
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
    }
  }
`

export const ChatComposer = styled.footer`
  display: flex;
  box-sizing: border-box;
  min-width: 0;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px 14px;
  border-top: 1px solid #eef0f3;
  background: #fff;
`

export const ComposerContext = styled.div`
  display: inline-flex;
  box-sizing: border-box;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #747b86;
  font-size: 11px;
  line-height: 1.3;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const ComposerInputRow = styled.div<{ $hasControls: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${({ $hasControls }) => $hasControls ? '40px 40px' : '40px'};
  align-items: end;
  gap: 8px;
  min-width: 0;

  .ant-input,
  .ant-input-textarea,
  .ant-input-textarea textarea {
    min-width: 0;
    min-height: 40px;
    padding: 8px 0;
    border: 0 !important;
    border-radius: 0;
    background: transparent !important;
    box-shadow: none !important;
    resize: none;
  }

  .ant-input:focus,
  .ant-input:focus-within,
  .ant-input-textarea textarea:focus {
    box-shadow: none !important;
  }

  .ant-btn {
    width: 40px;
    height: 40px;
    border-radius: 11px;
  }

  .ant-btn-primary:not(:disabled) {
    background: var(--blue-darker);
  }

  .ant-btn-primary:disabled {
    border-color: #e5e7eb;
    background: #e5e7eb;
    color: #8b929d;
  }

  @media (max-width: 767px) {
    textarea.ant-input {
      font-size: 16px;
    }
  }
`

export const ComposerNotice = styled.p`
  margin: 0; color: #7a818c; font-size: 10px; line-height: 1.5;
`
